import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Clock, CheckCircle, BookOpen } from 'lucide-react';
import Image from 'next/image';
import { MerchandiseUpsell } from '@/components/products/MerchandiseUpsell';
import { useSiteFlags } from '@/context/SiteFlagsContext';
import { STORY_TEXT_TIME } from '@/lib/seo/site-facts';

interface CoverPreviewOverlayProps {
  coverData: {
    scene?: string;
    outfit?: string;
    theme?: string;
    imageUrl?: string; // NEW: Actual cover image URL
    visualHints?: {
      setting?: string;
      mood?: string;
      keyElements?: string[];
    };
    generationTime?: number;
  };
  isGeneratingStory: boolean;
  estimatedTimeRemaining?: number;
  childName?: string;
  bookId?: string;
}

export function CoverPreviewOverlay({
  coverData,
  isGeneratingStory,
  estimatedTimeRemaining = 30,
  childName,
  bookId,
}: CoverPreviewOverlayProps) {
  const { merchEnabled } = useSiteFlags();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 shadow-lg border border-purple-200 max-w-2xl"
    >
      {/* Success Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-green-100 rounded-full p-2">
          <CheckCircle className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Cover Generated in {coverData.generationTime ? `${(coverData.generationTime / 1000).toFixed(1)}s` : 'record time'}!
          </h3>
          <p className="text-sm text-gray-600">
            Your book cover is ready while the full story generates
          </p>
        </div>
      </div>

      {/* Cover Image Display - FIXED: Proper aspect ratio and alignment */}
      {coverData.imageUrl && (
        <div className="mb-4 rounded-lg overflow-hidden shadow-md">
          <div className="relative w-full" style={{ aspectRatio: '2/3' }}>
            <Image
              src={coverData.imageUrl}
              alt="Your book cover"
              fill
              className="object-contain object-top"
              priority
              onError={(e) => {
                console.error('[COVER-PREVIEW] Image failed to load:', coverData.imageUrl);
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </div>
        </div>
      )}

      {/* Cover Details */}
      <div className="bg-white rounded-lg p-4 mb-4 space-y-3">
        {coverData.scene && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-1">Cover Scene</h4>
            <p className="text-sm text-gray-600 italic">"{coverData.scene}"</p>
          </div>
        )}
        
        {coverData.outfit && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-1">Character Outfit</h4>
            <p className="text-sm text-gray-600">{coverData.outfit}</p>
          </div>
        )}
        
        {coverData.theme && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-1">Story Theme</h4>
            <p className="text-sm text-gray-600">{coverData.theme}</p>
          </div>
        )}
        
        {coverData.visualHints && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-1">Visual Elements</h4>
            <div className="flex flex-wrap gap-2 mt-1">
              <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                {coverData.visualHints.setting}
              </span>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                {coverData.visualHints.mood}
              </span>
              {coverData.visualHints.keyElements?.map((element, idx) => (
                <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                  {element}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Story Generation Status */}
      {isGeneratingStory && (
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                Full story generating in background...
              </p>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="w-4 h-4 text-blue-600" />
                <p className="text-xs text-blue-700">
                  Usually takes {STORY_TEXT_TIME}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Merchandise Upsell - Show when cover image is ready */}
      {merchEnabled && coverData.imageUrl && childName && (
        <MerchandiseUpsell
          coverImageUrl={coverData.imageUrl}
          childName={childName}
          bookId={bookId}
          placement="cover-preview"
        />
      )}

      {/* Feature Badge */}
      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-purple-600">
        <Sparkles className="w-4 h-4" />
        <span className="font-medium">Cover-First Generation Active</span>
      </div>
    </motion.div>
  );
}
